import { useApi } from "~/composables/useApi";
import { usePosthog } from "~/composables/usePosthog";
import type {
  CompanyRemunerationSettings,
  CompanyRemunerationSettingsUpsertPayload,
  ReferenceDocumentIdsResponse,
} from "@asap/shared/schemas/remuneration/company-remuneration-settings";
import type {
  RemunerationTemplate,
  RemunerationTemplateInsertPayload,
  RemunerationTemplateUpdatePayload,
} from "@asap/shared/schemas/remuneration/remuneration-template";
import { isAbortError } from "~/utils/error";

export function useRemuneration() {
  const { invoke } = useApi();
  const { trackEvent } = usePosthog();

  const trackContractRemunerationBlockStarted = (params: { companyId?: string }): void => {
    void trackEvent("contract_remuneration_block_started", {
      company_id: params.companyId,
      is_structured_remuneration_block: true,
    });
  };

  const trackContractRemunerationTemplateSelectOpened = (params: { companyId: string }): void => {
    void trackEvent("contract_remuneration_template_select_opened", {
      company_id: params.companyId,
      is_structured_remuneration_block: true,
    });
  };

  const trackContractRemunerationTemplateSelected = (params: {
    companyId: string;
    template: RemunerationTemplate;
  }): void => {
    void trackEvent("contract_remuneration_template_selected", {
      company_id: params.companyId,
      remuneration_template_id: params.template.id,
      count: params.template.profile.indemnities.length,
      hourly_rate: params.template.profile.hourly_rate,
      hours_coefficient: params.template.profile.hours_coefficient,
      is_structured_remuneration_block: true,
    });
  };

  const trackContractCompensationBonusOthersSubmitted = (params: {
    companyId?: string;
    isStructuredRemunerationBlock: boolean;
    isDraft: boolean;
    characterCount: number;
  }): void => {
    void trackEvent("contract_compensation_bonus_others_submitted", {
      company_id: params.companyId,
      is_structured_remuneration_block: params.isStructuredRemunerationBlock,
      is_draft: params.isDraft,
      character_count: params.characterCount,
    });
  };

  const listTemplates = async function (
    companyId: string,
    params: {
      organizationId: string;
      pageIndex?: number;
      pageSize?: number;
      orderColumn?: string;
      orderDirection?: "asc" | "desc";
    },
    signal?: AbortSignal
  ): Promise<{ items: RemunerationTemplate[]; totalItems: number }> {
    const response = await invoke({
      path: "remuneration-templates",
      method: "GET",
      query: { companyId, ...params },
      signal,
    });

    if (!response.success) {
      if (isAbortError(response.error)) return { items: [], totalItems: 0 };
      throw new Error(response.error.message);
    }

    return {
      items: response.data.items,
      totalItems: response.data.totalItems,
    };
  };

  const createTemplate = async function (payload: RemunerationTemplateInsertPayload): Promise<RemunerationTemplate> {
    const response = await invoke({
      path: "remuneration-templates",
      method: "POST",
      body: payload,
    });

    if (!response.success) throw new Error(response.error.message);

    return response.data;
  };

  const updateTemplate = async function (
    id: string,
    payload: RemunerationTemplateUpdatePayload
  ): Promise<RemunerationTemplate> {
    const response = await invoke({
      path: "remuneration-templates/:id",
      method: "PATCH",
      params: { id },
      body: payload,
    });

    if (!response.success) throw new Error(response.error.message);

    return response.data;
  };

  const deleteTemplate = async function (id: string): Promise<void> {
    const response = await invoke({
      path: "remuneration-templates/:id",
      method: "DELETE",
      params: { id },
    });

    if (!response.success) throw new Error(response.error.message);
  };

  const getSettings = async function (companyId: string): Promise<CompanyRemunerationSettings | null> {
    const response = await invoke({
      path: "companies/:companyId/remuneration-settings",
      method: "GET",
      params: { companyId },
    });

    if (!response.success) throw new Error(response.error.message);

    return response.data.settings;
  };

  const updateSettings = async function (
    companyId: string,
    payload: CompanyRemunerationSettingsUpsertPayload
  ): Promise<CompanyRemunerationSettings> {
    const response = await invoke({
      path: "companies/:companyId/remuneration-settings",
      method: "PUT",
      params: { companyId },
      body: payload,
    });

    if (!response.success) throw new Error(response.error.message);

    return response.data;
  };

  const getReferenceDocumentIds = async function (companyId: string): Promise<ReferenceDocumentIdsResponse> {
    const response = await invoke({
      path: "companies/:companyId/reference-documents",
      method: "GET",
      params: { companyId },
    });

    if (!response.success) throw new Error(response.error.message);

    return response.data;
  };

  return {
    listTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getSettings,
    updateSettings,
    getReferenceDocumentIds,
    trackContractRemunerationBlockStarted,
    trackContractRemunerationTemplateSelectOpened,
    trackContractRemunerationTemplateSelected,
    trackContractCompensationBonusOthersSubmitted,
  };
}
